// Pure helpers for GameReleasesReport: release-type metadata, filtering, and
// the per-type comparison summary.

export const RELEASE_TYPES = [
  // VIP-only is always a phase of a pre-release (usually its first week,
  // sometimes all of it), never a release type of its own.
  { key: 'pre_release_vip', label: 'Pre-release + VIP', variant: 'purple' },
  { key: 'pre_release',     label: 'Pre-release',       variant: 'info' },
  { key: 'standard',    label: 'Standard',    variant: 'secondary' },
  // Released before the games API started tracking releases: the release
  // date is the catalog's first-seen date and the pre-release status is
  // simply not known.
  { key: 'unknown',     label: 'Untracked',   variant: 'outline' },
];

// Measurement windows: each game's own pre-release period, or the first N
// days after release (for comparing against games that weren't pre-released).
export const PRE_RELEASE_WINDOW = 'pre_release';
export const WINDOWS = [
  { key: PRE_RELEASE_WINDOW, label: 'Pre-release period' },
  { key: '7',  label: 'First 7 days' },
  { key: '14', label: 'First 14 days' },
  { key: '30', label: 'First 30 days' },
];

export function releaseTypeMeta(key) {
  return RELEASE_TYPES.find(t => t.key === key) || RELEASE_TYPES[RELEASE_TYPES.length - 1];
}

// 'YYYY-MM-DD' from an ISO timestamp, or null.
export function isoDay(value) {
  return value ? String(value).slice(0, 10) : null;
}

// "30 Jul – 6 Aug" for a start/end pair; null when the phase didn't happen.
export function windowLabel(start, end) {
  if (!start) return null;
  const fmt = v => new Date(`${isoDay(v)}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return end ? `${fmt(start)} – ${fmt(end)}` : `from ${fmt(start)}`;
}

// Rows whose release type is in `types` (a Set) and, unless 'all', whose
// provider matches.
export function filterReleases(rows, { types, provider }) {
  return rows.filter(r => types.has(r.release_type) && (provider === 'all' || r.provider === provider));
}

export function median(values) {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function mean(values) {
  return values.length === 0 ? null : values.reduce((s, v) => s + v, 0) / values.length;
}

// A game is measured once its whole window has elapsed; games without a
// window (not pre-released, in pre-release-period mode) aren't measured.
export function windowComplete(r) {
  return r.window_start != null && r.window_length > 0 && r.days_observed >= r.window_length;
}

// A finished window with no players at all: most likely a catalog entry that
// never went live on the site.
export function neverPlayed(r) {
  return windowComplete(r) && !(r.players > 0);
}

// Whether the summary averages the game: its window has finished and it was
// actually played — a never-live entry counted as 0 would drag the average
// down.
export function inSummary(r) {
  return windowComplete(r) && r.players > 0;
}

// Which games the table lists: those averaged in the summary (window
// finished), those left out (still running, or no window in this phase), or
// all of them.
export const LIST_SCOPES = [
  { key: 'in_summary',     label: 'In summary' },
  { key: 'not_in_summary', label: 'Not in summary' },
  { key: 'all',            label: 'All' },
];

export function filterByScope(rows, scope) {
  if (scope === 'in_summary') return rows.filter(inSummary);
  if (scope === 'not_in_summary') return rows.filter(r => !inSummary(r));
  return rows;
}

// One summary row per release type present in rows, in RELEASE_TYPES order.
// Only games whose full window has elapsed and that were played are compared
// — a game three days into its window, or one never live, would otherwise
// drag its type's averages down — and `pending` counts the ones left out
// (still running or never played). Pre-release periods differ in
// length, so turnover is also given per day of the window.
export function summarizeByType(rows) {
  return RELEASE_TYPES.map(type => {
    const measured = rows.filter(r => r.release_type === type.key && r.window_start != null);
    const complete = measured.filter(inSummary);
    const turnover = complete.map(r => r.turnover_eur);
    const ggr = complete.map(r => r.ggr_eur);
    const players = complete.map(r => r.players);
    const totalTurnover = turnover.reduce((s, v) => s + v, 0);
    const totalGgr = ggr.reduce((s, v) => s + v, 0);
    return {
      ...type,
      games: complete.length,
      pending: measured.length - complete.length,
      avg_days: mean(complete.map(r => r.window_length)),
      avg_players: mean(players),
      median_players: median(players),
      avg_turnover_eur: mean(turnover),
      median_turnover_eur: median(turnover),
      avg_turnover_per_day: mean(complete.map(r => r.turnover_eur / r.window_length)),
      avg_ggr_eur: mean(ggr),
      payout_pct: totalTurnover > 0 ? (100 * (totalTurnover - totalGgr)) / totalTurnover : null,
      vip_share: mean(complete.filter(r => r.players > 0).map(r => r.vip_players / r.players)),
    };
  }).filter(s => s.games + s.pending > 0);
}

const DAY_MS = 24 * 60 * 60 * 1000;

function wholeDaysBetween(start, end) {
  return Math.max(0, Math.floor((new Date(end) - new Date(start)) / DAY_MS));
}

// Days of the stats window inside the VIP-only window (clipped to the stats
// window) and outside it. All open for a game without a VIP-only window.
export function phaseDays(r) {
  if (!r.vip_only_start || !r.vip_only_end || !r.window_start) return { vip: 0, open: r.window_length };
  const start = Math.max(new Date(r.window_start), new Date(r.vip_only_start));
  const end = Math.min(new Date(r.window_end), new Date(r.vip_only_end));
  const vip = Math.min(r.window_length, wholeDaysBetween(start, end));
  return { vip, open: r.window_length - vip };
}

export function isPreReleaseType(type) {
  return type === 'pre_release_vip' || type === 'pre_release';
}

// Pre-release phases the stats can cover. A VIP phase is always the start of
// a pre-release (usually its first week, sometimes all of it); the rest of
// the pre-release is open to everyone.
export const PHASES = [
  { key: 'combined', label: 'Combined' },
  { key: 'vip',      label: 'VIP-only window' },
  { key: 'open',     label: 'Open pre-release' },
];

// A row with no stats window: the game didn't have the phase.
function unmeasured(r) {
  return { ...r, window_start: null, window_end: null, window_length: 0, days_observed: 0 };
}

function withPhaseStats(r, start, end, length, { players, vipPlayers, turnover, ggr }) {
  const offset = wholeDaysBetween(r.window_start, start);
  return {
    ...r,
    window_start: start.toISOString(),
    window_end: end.toISOString(),
    window_length: length,
    days_observed: Math.max(0, Math.min(length, r.days_observed - offset)),
    players,
    vip_players: vipPlayers,
    turnover_eur: turnover,
    ggr_eur: ggr,
    payout_pct: turnover > 0 ? (100 * (turnover - ggr)) / turnover : null,
  };
}

// The row re-measured over one phase of its stats window: the window, its
// length and elapsed days, and players/VIP players/turnover/GGR/payout all
// switch to that phase. A game that didn't have the phase (no VIP window, or
// VIP for the whole pre-release so no open part) comes back unmeasured; a
// pre-release without VIP is open throughout, so its open phase is all of it.
// Games that weren't pre-released have no phases (in pre-release-period mode
// they're measured over their first days instead), so they're unmeasured in
// the VIP and open tabs.
export function asPhase(r, phase) {
  if (phase === 'combined' || r.window_start == null) return r;
  if (!isPreReleaseType(r.release_type)) return unmeasured(r);
  const days = phaseDays(r);
  const windowEnd = new Date(r.window_end);
  const vipEnd = days.vip > 0 ? new Date(Math.min(windowEnd, new Date(r.vip_only_end))) : null;

  if (phase === 'vip') {
    if (days.vip === 0) return unmeasured(r);
    const vipStart = new Date(Math.max(new Date(r.window_start), new Date(r.vip_only_start)));
    return withPhaseStats(r, vipStart, vipEnd, days.vip, {
      players: r.vip_window_players, vipPlayers: r.vip_window_vip_players,
      turnover: r.vip_window_turnover_eur, ggr: r.vip_window_ggr_eur,
    });
  }
  if (days.open === 0) return unmeasured(r);
  return withPhaseStats(r, vipEnd || new Date(r.window_start), windowEnd, days.open, {
    players: r.open_players, vipPlayers: r.open_vip_players,
    turnover: r.open_turnover_eur, ggr: r.open_ggr_eur,
  });
}

function earliest(...dates) {
  const ds = dates.filter(Boolean).map(d => new Date(d));
  return ds.length ? new Date(Math.min(...ds)) : null;
}

function latest(...dates) {
  const ds = dates.filter(Boolean).map(d => new Date(d));
  return ds.length ? new Date(Math.max(...ds)) : null;
}

// A game's pre-release period [start, end) — end is the general release
// day — and its VIP-only week within it, or null for a game that wasn't
// pre-released. Like the backend, the period also covers the VIP window, so
// a row with missing pre-release dates (a known games-API data error) falls
// back to its VIP window.
export function preReleasePeriod(g) {
  const start = earliest(g.pre_release_start, g.vip_only_start);
  const end = latest(g.pre_release_end, g.vip_only_end);
  if (!start || !end) return null;
  const vip = g.vip_only_start && g.vip_only_end
    ? { start: new Date(g.vip_only_start), end: new Date(g.vip_only_end), days: wholeDaysBetween(g.vip_only_start, g.vip_only_end) }
    : null;
  return { start, end, days: wholeDaysBetween(start, end), vip };
}

// "3 Sept – 16 Sept" for an end-exclusive [start, end).
export function periodLabel(start, end) {
  const lastDay = new Date(end);
  lastDay.setUTCDate(lastDay.getUTCDate() - 1);
  return windowLabel(start.toISOString(), lastDay.toISOString());
}

// Launch-curve markers (day N = the Nth 24 hours since release) for when the
// VIP week ended and the general release began, within days 1..horizon.
export function phaseMarkers(g, horizon) {
  const period = preReleasePeriod(g);
  if (!period) return [];
  const dayOf = date => Math.max(1, Math.ceil((date - new Date(g.release_date)) / DAY_MS));
  const markers = [];
  if (period.vip && period.vip.end < period.end) markers.push({ key: 'vip_end', day: dayOf(period.vip.end), label: 'VIP week ends' });
  markers.push({ key: 'general', day: dayOf(period.end), label: 'General release' });
  return markers.filter(m => m.day <= horizon);
}

// Game Performance date range opening on the earliest selected release, so
// the compared games' launches are all in view.
export function comparisonRange(games, today) {
  const days = games.map(g => isoDay(g.release_date)).filter(Boolean).sort();
  const to = isoDay(today.toISOString());
  const from = days[0] && days[0] < to ? days[0] : to;
  return { from, to };
}
